import React from 'react';
import { Card, Input, Button, Space, Empty } from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { TextArea } = Input;

export interface FlashCard { front: string; back: string }
export interface TimelineEvent { period: string; title: string; description: string }

function moveItem<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr]; [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export const PocketCardsEditor: React.FC<{ cards: FlashCard[]; onChange: (cards: FlashCard[]) => void }> = ({ cards, onChange }) => (
  <div>
    {cards.map((c, i) => (
      <Card key={i} size="small" style={{ marginBottom: 10 }}
        title={<Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>Card {i + 1}</span>
          <Space size="small">
            <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => onChange(moveItem(cards, i, -1))} />
            <Button size="small" icon={<ArrowDownOutlined />} disabled={i === cards.length - 1} onClick={() => onChange(moveItem(cards, i, 1))} />
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onChange(cards.filter((_, ix) => ix !== i))} />
          </Space>
        </Space>}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            value={c.front} placeholder="Front (prompt/term/question)"
            onChange={(e) => onChange(cards.map((x, ix) => (ix === i ? { ...x, front: e.target.value } : x)))}
          />
          <TextArea
            rows={2} value={c.back} placeholder="Back (crisp, complete answer)"
            onChange={(e) => onChange(cards.map((x, ix) => (ix === i ? { ...x, back: e.target.value } : x)))}
          />
        </Space>
      </Card>
    ))}
    {cards.length === 0 && <Empty description="No flashcards yet" style={{ marginBottom: 12 }} />}
    <Button icon={<PlusOutlined />} onClick={() => onChange([...cards, { front: '', back: '' }])}>Add flashcard</Button>
  </div>
);

export const TimelineEditor: React.FC<{ events: TimelineEvent[]; onChange: (events: TimelineEvent[]) => void }> = ({ events, onChange }) => (
  <div>
    {events.map((e, i) => (
      <Card key={i} size="small" style={{ marginBottom: 10 }}
        title={<Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>Event {i + 1}</span>
          <Space size="small">
            <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => onChange(moveItem(events, i, -1))} />
            <Button size="small" icon={<ArrowDownOutlined />} disabled={i === events.length - 1} onClick={() => onChange(moveItem(events, i, 1))} />
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onChange(events.filter((_, ix) => ix !== i))} />
          </Space>
        </Space>}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            value={e.period} placeholder="Period / date (e.g. 14 July 1789)"
            onChange={(ev) => onChange(events.map((x, ix) => (ix === i ? { ...x, period: ev.target.value } : x)))}
          />
          <Input
            value={e.title} placeholder="Event title"
            onChange={(ev) => onChange(events.map((x, ix) => (ix === i ? { ...x, title: ev.target.value } : x)))}
          />
          <TextArea
            rows={2} value={e.description} placeholder="Description"
            onChange={(ev) => onChange(events.map((x, ix) => (ix === i ? { ...x, description: ev.target.value } : x)))}
          />
        </Space>
      </Card>
    ))}
    {events.length === 0 && <Empty description="No timeline events — this is fine for chapters with no real chronology" style={{ marginBottom: 12 }} />}
    <Button icon={<PlusOutlined />} onClick={() => onChange([...events, { period: '', title: '', description: '' }])}>Add event</Button>
  </div>
);
